/* eslint-disable @typescript-eslint/no-magic-numbers */
import { Dialog } from '@angular/cdk/dialog';
import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, Signal, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { pathParameter, queryParameter, TagEvent, TagPusherService } from '@libs/shared-services';
import { ButtonGroupModule, DEFAULT_TEETH, DropdownComponent, DropdownItem, PerioHistory, Quadrant, TooltipModule } from '@libs/ui-lib';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OnboardingModalService, OnboardingTab } from '@libs/onboarding';
import { isSameDay, routes } from '@libs/utils';
import { SvgIconComponent, SvgIcons } from '@ngneat/svg-icon';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ChartingScreenComponentService } from 'libs/charting/src/lib/components/charting-screen/charting-screen.component.service';
import { filter } from 'rxjs';
import { ProvisionalStatus } from '@ffd/api-client';
import { TeethComponent } from '../teeth';
import { fadeInOnly, growWidth } from '../../common/animations';
import { PerioExamScreenService } from '../perio-exam-screen/services/perio-exam/perio-exam-screen.service';
import { PerioScreenComponentService } from './perio-screen.component.service';
import {
  PerioExamNewExamConfirmationEnum,
  PerioNewExamConfirmationModalComponent,
} from './perio-new-exam-confirmation-modal/perio-new-exam-confirmation-modal.component';
import {
  PerioSameDayExamConfirmationData,
  PerioSameDayExamConfirmationEnum,
  PerioSameDayExamConfirmationModalComponent,
} from './perio-same-day-exam-confirmation-modal/perio-same-day-exam-confirmation-modal.component';
import {
  PerioEditExamConfirmationModalComponent,
  PerioExamEditExamConfirmationEnum,
} from './perio-edit-exam-confirmation-modal/perio-edit-exam-confirmation-modal.component';

export enum SelectedQuadrant {
  Full = ' ',
  UpperLeft = 'Upper Left Quadrant',
  UpperRight = 'Upper Right Quadrant',
  LowerLeft = 'Lower Left Quadrant',
  LowerRight = 'Lower Right Quadrant',
}

export enum SelectedCompare {
  PocketDepth = 'Pocket Depth',
  RecessionAndMobility = 'Recession and Mobility',
}

@Component({
  selector: 'lib-perio',
  imports: [
    CommonModule,
    FormsModule,
    DropdownComponent,
    TranslateModule,
    SvgIconComponent,
    ButtonGroupModule,
    RouterModule,
    TeethComponent,
    TooltipModule,
  ],
  providers: [DatePipe, ChartingScreenComponentService],
  templateUrl: './perio-screen.component.html',
  styleUrl: './perio-screen.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [growWidth, fadeInOnly],
})
export class PerioScreenComponent implements OnInit, OnDestroy {
  @ViewChild(TeethComponent) public perioChart!: TeethComponent;
  public SelectedQuadrant = SelectedQuadrant;
  public Quadrant = Quadrant;
  public selectedQuadrant = signal<SelectedQuadrant>(SelectedQuadrant.Full);

  private readonly route = inject(ActivatedRoute);
  private readonly datePipe = inject(DatePipe);
  private readonly screenService = inject(PerioScreenComponentService);
  private readonly examScreenService = inject(PerioExamScreenService);
  private readonly chartingScreenService = inject(ChartingScreenComponentService);
  private readonly dialog = inject(Dialog);
  private readonly onboardingModalService = inject(OnboardingModalService);
  private readonly tagPusherService = inject(TagPusherService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);

  public readonly params = toSignal(this.route.paramMap);
  public readonly selectionDate = signal<number>(0);
  public readonly previousSelectionDate = signal<number>(0);
  public readonly defaultTeeth = signal(DEFAULT_TEETH);
  protected compareSelected = signal<SelectedCompare | null>(null);
  protected hasExamDraft = toSignal<boolean>(this.examScreenService.hasDraft$);
  protected perioSavingInProgress = this.examScreenService.perioSavingInProgress;

  public readonly loading = computed(() => this.screenService.viewModel().loading);
  public readonly perioExams = computed(() => this.screenService.viewModel().perioExams);
  public perioExams$ = toObservable(this.perioExams);
  public readonly selectedPerioExam = computed(() => this.perioExams().find((e) => e.date === this.selectionDate()));
  public readonly selectedPreviousPerioExam = computed(() => this.perioExams().find((e) => e.date === this.previousSelectionDate()));
  protected readonly editPerioExamFeatureEnabled = computed(() => this.examScreenService.viewModel().editPerioExamFeatureEnabled);
  protected readonly perioExamDropdownPrefixIcon = computed(() => {
    const selectedExam = this.selectedPerioExam();
    return selectedExam ? this.getPerioHistoryProvisionalIcon(selectedExam) : null;
  });
  protected readonly perioExamPrefixIconTooltip = computed(() =>
    this.perioExamDropdownPrefixIcon() ? this.translateService.instant('perio_provisional_dropdown_tooltip') : null,
  );

  protected isShowcase = this.route.snapshot.paramMap.get(pathParameter('siteId')) === routes.showcaseSiteId;
  protected readonly routes = routes;
  protected readonly ProvisionalStatus = ProvisionalStatus;
  protected readonly SelectedCompare = SelectedCompare;

  public readonly perioExamOptions: Signal<DropdownItem<number>[]> = computed(() => {
    return this.perioExams().map((exam) => ({
      value: exam.date,
      label: this.datePipe.transform(exam.date, 'MM/dd/yyyy') || '',
      prefixIcon: this.getPerioHistoryProvisionalIcon(exam) ?? undefined,
    }));
  });
  public readonly previousPerioExamOptionsSignal: Signal<DropdownItem<number>[]> = computed(() => {
    return this.perioExamOptions().slice(1);
  });
  public quadrantAnimationInProgress = false;

  public selectTransformOrigin(quadrant: SelectedQuadrant): void {
    switch (quadrant) {
      case SelectedQuadrant.UpperLeft: {
        this.perioChart.zoomToTopLeft();
        break;
      }
      case SelectedQuadrant.UpperRight: {
        this.perioChart.zoomToTopRight();
        break;
      }
      case SelectedQuadrant.LowerLeft: {
        this.perioChart.zoomToBottomLeft();
        break;
      }
      case SelectedQuadrant.LowerRight: {
        this.perioChart.zoomToBottomRight();
        break;
      }
      case SelectedQuadrant.Full: {
        this.perioChart.zoomToCenter();
        break;
      }
    }
  }
  private zoomCount = 0;

  constructor() {
    this.perioExams$.pipe(filter(() => !this.loading())).subscribe((exams) => {
      if (exams.length > 0 && !exams.some((exam) => exam.date === this.selectionDate())) {
        this.selectionDate.set(exams[0].date);
      }
      if (exams.length > 1) {
        this.previousSelectionDate.set(exams[1].date);
      }
    });
  }

  public ngOnInit(): void {
    const siteId = this.route.snapshot.paramMap.get(pathParameter('siteId'));
    const patientId = this.route.snapshot.paramMap.get(pathParameter('patientId'));
    const needPerioExamRefetch = !(this.route.snapshot.queryParamMap.get(queryParameter('needDataRefetch')) === 'false');
    const selectedPerioExamDate = this.route.snapshot.queryParamMap.get(queryParameter('selectedPerioExamDate'));

    if (selectedPerioExamDate) {
      this.selectionDate.set(Number.parseFloat(selectedPerioExamDate));
      // Reset the query param to avoid affecting future navigations
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { selectedPerioExamDate: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }

    this.screenService.init(siteId ?? undefined, patientId ?? undefined, needPerioExamRefetch);
    this.examScreenService.init();

    this.chartingScreenService.init(siteId ?? undefined, patientId ?? undefined);

    this.onboardingModalService.setActiveTab(OnboardingTab.Perio);
    this.tagPusherService.pushTag({
      event: TagEvent.PerioChartingOpened,
    });
  }

  public ngOnDestroy(): void {
    this.selectionDate.set(0);
  }

  public selectQuadrant(quadrant: SelectedQuadrant): void {
    if (this.quadrantAnimationInProgress) {
      return;
    }

    if (this.selectedQuadrant() === quadrant) {
      quadrant = SelectedQuadrant.Full;
    }

    this.selectedQuadrant.set(quadrant);
    this.quadrantAnimationInProgress = true;
    this.selectTransformOrigin(quadrant);

    if (quadrant !== SelectedQuadrant.Full) {
      this.zoomCount++;
    }

    setTimeout(() => {
      this.quadrantAnimationInProgress = false;
    }, 1000);
  }

  public onSelectionChange(selection: number): void {
    this.selectionDate.set(selection);
  }

  public onPreviousSelectionChange(selection: number): void {
    this.previousSelectionDate.set(selection);
  }

  public onToothSelected(toothPosition: number): void {
    const siteId = this.params()!.get(pathParameter('siteId')) ?? undefined;
    const patientId = this.params()!.get(pathParameter('patientId')) ?? undefined;
    const teethWithLayers = this.chartingScreenService.viewModel().teethWithLayers;
    if (!siteId || !patientId || !teethWithLayers) return;

    this.router.navigate([routes.toothDetails, toothPosition], { relativeTo: this.route, queryParams: { date: this.selectionDate() } });
  }

  protected onCompareClick(): void {
    this.compareSelected.set(SelectedCompare.PocketDepth);
  }

  protected onEditClick(): void {
    this.tagPusherService.pushTag({
      event: TagEvent.PerioExamStarted,
    });

    if (!this.hasExamDraft()) {
      this.navigateToPerioExamEditForm(this.selectionDate());
      return;
    }

    this.dialog
      .open<PerioExamEditExamConfirmationEnum>(PerioEditExamConfirmationModalComponent, {
        backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      })
      .closed.subscribe((val) => {
        if (val === PerioExamEditExamConfirmationEnum.FinishAndEdit) {
          const examDraft = this.examScreenService.getDraft();
          this.examScreenService.completePerioExam(examDraft?.isEditing);
          this.navigateToPerioExamEditForm(this.selectionDate());
        }

        if (val === PerioExamEditExamConfirmationEnum.Cancel) {
          return;
        }
      });
  }

  protected onPocketDepthClick(): void {
    // set selected date compared to the latest perio exam
    if (this.selectionDate() !== this.perioExams()[0].date) {
      this.previousSelectionDate.set(this.selectionDate());
      this.selectionDate.set(this.perioExams()[0].date);
    }
    this.compareSelected.set(SelectedCompare.PocketDepth);
  }

  protected onCloseCompareClick(): void {
    this.selectionDate.set(this.perioExams()[0].date);
    this.compareSelected.set(null);
  }

  protected onNewExamClick(): void {
    if (isSameDay(new Date(), new Date(this.perioExams()[0]?.date))) {
      this.dialog
        .open<PerioSameDayExamConfirmationEnum, PerioSameDayExamConfirmationData>(PerioSameDayExamConfirmationModalComponent, {
          backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
          data: {
            sameDayInProgress: this.perioExams()[0]?.provisionalStatus !== ProvisionalStatus.READY,
          },
        })
        .closed.subscribe((val) => {
          if (val === PerioSameDayExamConfirmationEnum.Edit) {
            this.examScreenService.removeDraft();
            this.navigateToPerioExamEditForm(this.selectionDate());
            this.tagPusherService.pushTag({
              event: TagEvent.PerioExamStarted,
            });
          }
        });
    } else if (this.hasExamDraft()) {
      this.dialog
        .open<PerioExamNewExamConfirmationEnum>(PerioNewExamConfirmationModalComponent, {
          backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
        })
        .closed.subscribe((val) => {
          if (val === PerioExamNewExamConfirmationEnum.New) {
            this.examScreenService.removeDraft();
            this.navigateToPerioExamManualForm();
            this.tagPusherService.pushTag({
              event: TagEvent.PerioExamStarted,
            });
          }

          if (val === PerioExamNewExamConfirmationEnum.Resume) {
            this.onResumeExamClick();
          }
        });
    } else {
      this.navigateToPerioExamManualForm();
    }
  }

  protected onResumeExamClick(): void {
    const examDraft = this.examScreenService.getDraft();
    if (examDraft && examDraft.isEditing) {
      this.navigateToPerioExamEditForm(examDraft.originalExamData);
    } else {
      this.navigateToPerioExamManualForm();
    }
  }

  private navigateToPerioExamEditForm(examDate: number) {
    this.router.navigate([routes.editExam, examDate], { relativeTo: this.route });
  }

  private getPerioHistoryProvisionalIcon(perioExam: PerioHistory): SvgIcons | null {
    if (perioExam.provisionalStatus === ProvisionalStatus.EDITING) {
      return 'px-16-edit-pending';
    }

    if (perioExam.provisionalStatus === ProvisionalStatus.DELETING) {
      return 'px-16-delete-pending';
    }

    if (perioExam.provisionalStatus === ProvisionalStatus.CREATING) {
      return 'px-24-hourglass';
    }

    return null;
  }

  private navigateToPerioExamManualForm() {
    this.router.navigate([routes.newExam], { relativeTo: this.route });
  }
}
